import React, { useState } from 'react';
import { ArrowRight, ArrowLeft, Check, Loader, ChevronRight } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const OnboardingQuiz = ({ onComplete, onSkip }) => {
  const { register } = useAuth();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [answers, setAnswers] = useState({});
  const [showSignup, setShowSignup] = useState(false);
  const [signupData, setSignupData] = useState({ name: '', email: '', password: '' });
  const [error, setError] = useState('');

  const questions = [
    {
      id: 'goal',
      question: "Quel est votre objectif principal ?",
      subtitle: "Nous adapterons nos conseils en fonction de votre priorité",
      type: 'single',
      options: [
        { id: 'weight-loss', emoji: '⚖️', label: 'Perdre du poids', desc: 'Atteindre un poids qui me convient' },
        { id: 'health', emoji: '💚', label: 'Manger plus sainement', desc: 'Améliorer mon alimentation au quotidien' },
        { id: 'energy', emoji: '⚡', label: 'Avoir plus d\'énergie', desc: 'Me sentir moins fatigué(e)' },
        { id: 'muscle', emoji: '💪', label: 'Me tonifier', desc: 'Gagner en masse musculaire' },
      ]
    },
    {
      id: 'weight_to_lose',
      question: "Combien de kilos souhaitez-vous perdre ?",
      subtitle: "Soyez réaliste, chaque kilo compte",
      type: 'single',
      condition: (ans) => ans.goal === 'weight-loss',
      options: [
        { id: '1-5', emoji: '🎯', label: 'Moins de 5 kg', desc: 'Quelques kilos en trop' },
        { id: '5-10', emoji: '📉', label: 'Entre 5 et 10 kg', desc: 'Un objectif atteignable' },
        { id: '10-20', emoji: '🏃', label: 'Entre 10 et 20 kg', desc: 'Un vrai projet' },
        { id: '20+', emoji: '🌟', label: 'Plus de 20 kg', desc: 'Une belle transformation' },
      ]
    },
    {
      id: 'tried_before',
      question: "Avez-vous déjà tenté de modifier vos habitudes alimentaires ?",
      subtitle: "Votre expérience nous aide à mieux vous accompagner",
      type: 'single',
      options: [
        { id: 'never', emoji: '🆕', label: 'C\'est la première fois', desc: 'Je débute' },
        { id: 'few', emoji: '🔄', label: 'Quelques tentatives', desc: 'J\'ai essayé 1 ou 2 fois' },
        { id: 'many', emoji: '😤', label: 'Plusieurs fois', desc: 'Sans succès durable' },
        { id: 'yoyo', emoji: '📊', label: 'L\'effet yoyo', desc: 'Je perds puis je reprends' },
      ]
    },
    {
      id: 'obstacle',
      question: "Quel est votre plus grand frein au quotidien ?",
      subtitle: "Identifier l'obstacle, c'est commencer à le surmonter",
      type: 'single',
      options: [
        { id: 'time', emoji: '⏰', label: 'Le manque de temps', desc: 'Entre le travail et la vie perso...' },
        { id: 'motivation', emoji: '😔', label: 'La motivation', desc: 'Je commence mais j\'abandonne' },
        { id: 'cravings', emoji: '🍫', label: 'Les envies sucrées', desc: 'Difficile de résister' },
        { id: 'knowledge', emoji: '🤔', label: 'Le manque d\'information', desc: 'Je ne sais pas quoi manger' },
        { id: 'social', emoji: '🍽️', label: 'Les repas sociaux', desc: 'Famille, restos, apéros...' },
      ]
    },
    {
      id: 'eating_habits',
      question: "Comment décririez-vous vos habitudes actuelles ?",
      subtitle: "Pas de jugement, juste un état des lieux",
      type: 'single',
      options: [
        { id: 'chaotic', emoji: '🌪️', label: 'C\'est un peu le chaos', desc: 'Je mange ce que je trouve' },
        { id: 'average', emoji: '😐', label: 'Correctes mais perfectibles', desc: 'Je fais attention parfois' },
        { id: 'good', emoji: '👍', label: 'Plutôt bonnes', desc: 'Je veux juste optimiser' },
        { id: 'emotional', emoji: '💭', label: 'Je mange selon mes émotions', desc: 'Stress, fatigue, ennui...' },
      ]
    },
    {
      id: 'cooking_time',
      question: "Combien de temps pouvez-vous consacrer à la cuisine ?",
      subtitle: "Par repas, en moyenne",
      type: 'single',
      options: [
        { id: '10min', emoji: '⚡', label: 'Moins de 15 minutes', desc: 'Je veux du rapide' },
        { id: '30min', emoji: '👍', label: '15 à 30 minutes', desc: 'Un juste milieu' },
        { id: '45min', emoji: '👨‍🍳', label: '30 à 45 minutes', desc: 'J\'aime cuisiner' },
        { id: '60min', emoji: '❤️', label: 'Plus de 45 minutes', desc: 'C\'est un plaisir' },
      ]
    },
    {
      id: 'weak_moment',
      question: "À quel moment de la journée craquez-vous le plus ?",
      subtitle: "On va travailler ensemble sur ce point",
      type: 'single',
      options: [
        { id: 'morning', emoji: '🌅', label: 'Le matin', desc: 'Petit-déjeuner trop copieux' },
        { id: 'afternoon', emoji: '☀️', label: 'L\'après-midi', desc: 'Le coup de mou de 16h' },
        { id: 'evening', emoji: '🌙', label: 'Le soir', desc: 'Devant la télé ou après dîner' },
        { id: 'never', emoji: '✨', label: 'Pas de moment particulier', desc: 'C\'est plutôt aléatoire' },
      ]
    },
    {
      id: 'age_range',
      question: "Dans quelle tranche d'âge êtes-vous ?",
      subtitle: "Les besoins nutritionnels varient avec l'âge",
      type: 'single',
      options: [
        { id: '25-35', emoji: '🌱', label: '25-35 ans', desc: '' },
        { id: '35-45', emoji: '🌿', label: '35-45 ans', desc: '' },
        { id: '45-55', emoji: '🌳', label: '45-55 ans', desc: '' },
        { id: '55+', emoji: '🍂', label: '55 ans et plus', desc: '' },
      ]
    },
    {
      id: 'support_style',
      question: "Quel type d'accompagnement vous convient le mieux ?",
      subtitle: "Pour personnaliser les messages de Louis",
      type: 'single',
      options: [
        { id: 'gentle', emoji: '🤗', label: 'Bienveillant', desc: 'Encouragements, compréhension' },
        { id: 'direct', emoji: '💬', label: 'Direct', desc: 'Concret, sans détour' },
        { id: 'coach', emoji: '🔥', label: 'Motivant', desc: 'Comme un coach sportif' },
        { id: 'expert', emoji: '🎓', label: 'Pédagogue', desc: 'Avec des explications' },
      ]
    },
  ];

  // Filtrer les questions selon les conditions
  const activeQuestions = questions.filter(q => {
    if (!q.condition) return true;
    return q.condition(answers);
  });

  const currentQuestion = activeQuestions[step];
  const totalSteps = activeQuestions.length;
  const progress = ((step + 1) / totalSteps) * 100;

  const handleAnswer = (optionId) => {
    setAnswers({ ...answers, [currentQuestion.id]: optionId });
    
    setTimeout(() => {
      if (step < activeQuestions.length - 1) {
        setStep(step + 1);
      } else {
        setShowSignup(true);
      }
    }, 300);
  };

  const handleBack = () => {
    if (showSignup) {
      setShowSignup(false);
    } else if (step > 0) {
      setStep(step - 1);
    }
  };

  const requestNotificationPermission = async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      try {
        const permission = await Notification.requestPermission();
        if (permission === 'granted') {
          // Enregistrer le service worker pour les push
          if ('serviceWorker' in navigator) {
            navigator.serviceWorker.register('/sw.js').catch(() => {});
          }
        }
      } catch (e) {
        console.log('Notifications non supportées');
      }
    }
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!signupData.name || !signupData.email || !signupData.password) {
      setError('Veuillez remplir tous les champs');
      setLoading(false);
      return;
    }

    if (signupData.password.length < 6) {
      setError('Mot de passe : 6 caractères minimum');
      setLoading(false);
      return;
    }

    const result = await register({
      ...signupData,
      goal: answers.goal,
      quizAnswers: answers
    });

    if (result.success) {
      // Demander les notifications après inscription réussie
      await requestNotificationPermission();
      onComplete(answers);
    } else {
      if (result.error.includes('already registered')) {
        setError('Cet email est déjà utilisé');
      } else {
        setError(result.error || 'Erreur lors de l\'inscription');
      }
    }
    setLoading(false);
  };

  const getPersonalizedPlan = () => {
    const plan = [];
    
    // Selon l'objectif
    if (answers.goal === 'weight-loss') {
      plan.push({ icon: '📉', text: 'Programme perte de poids personnalisé' });
    } else if (answers.goal === 'health') {
      plan.push({ icon: '🥗', text: 'Conseils pour manger plus sainement' });
    } else if (answers.goal === 'energy') {
      plan.push({ icon: '⚡', text: 'Aliments pour booster votre énergie' });
    } else {
      plan.push({ icon: '💪', text: 'Alimentation adaptée à la prise de muscle' });
    }

    // Selon l'obstacle
    if (answers.obstacle === 'time') {
      plan.push({ icon: '⏱️', text: 'Recettes express (moins de 15 min)' });
    } else if (answers.obstacle === 'cravings') {
      plan.push({ icon: '🍫', text: 'Stratégies anti-fringales efficaces' });
    } else if (answers.obstacle === 'motivation') {
      plan.push({ icon: '🎯', text: 'Suivi quotidien pour rester motivé' });
    } else if (answers.obstacle === 'knowledge') {
      plan.push({ icon: '📚', text: 'Explications simples et concrètes' });
    }

    // Selon le moment faible
    if (answers.weak_moment === 'evening') {
      plan.push({ icon: '🌙', text: 'Astuces spéciales pour les soirées' });
    } else if (answers.weak_moment === 'afternoon') {
      plan.push({ icon: '☀️', text: 'Collations saines pour l\'après-midi' });
    }

    // Toujours inclus
    plan.push({ icon: '💬', text: 'Louis, votre coach disponible 24h/24' });

    return plan;
  };

  // Écran inscription
  if (showSignup) {
    const plan = getPersonalizedPlan();
    const successRate = answers.tried_before === 'yoyo' || answers.tried_before === 'many' ? 87 : 94;

    return (
      <div className="onboarding-quiz">
        <div className="quiz-progress-bar">
          <div className="progress-fill" style={{ width: '100%' }}></div>
        </div>

        <button className="quiz-back-btn" onClick={handleBack}>
          <ArrowLeft size={20} />
        </button>

        <div className="quiz-final">
          <div className="final-header">
            <span className="final-emoji">🎉</span>
            <h1>Votre programme est prêt !</h1>
            <p>Basé sur vos réponses, voici ce que nous avons préparé pour vous</p>
          </div>

          <div className="final-stats">
            <div className="stat-circle">
              <span className="stat-value">{successRate}%</span>
            </div>
            <p>des personnes avec un profil similaire au vôtre atteignent leurs objectifs avec MonCoach</p>
          </div>

          <div className="final-plan">
            <h3>Votre programme inclut :</h3>
            {plan.map((item, i) => (
              <div key={i} className="plan-item">
                <span className="plan-icon">{item.icon}</span>
                <span>{item.text}</span>
              </div>
            ))}
          </div>

          <form className="final-form" onSubmit={handleSignup}>
            <h3>Créez votre compte gratuit</h3>
            
            <div className="form-field">
              <input
                type="text"
                placeholder="Votre prénom"
                value={signupData.name}
                onChange={(e) => setSignupData({...signupData, name: e.target.value})}
                autoComplete="given-name"
              />
            </div>
            
            <div className="form-field">
              <input
                type="email"
                placeholder="Votre adresse email"
                value={signupData.email}
                onChange={(e) => setSignupData({...signupData, email: e.target.value})}
                autoComplete="email"
              />
            </div>
            
            <div className="form-field">
              <input
                type="password"
                placeholder="Créer un mot de passe"
                value={signupData.password}
                onChange={(e) => setSignupData({...signupData, password: e.target.value})}
                autoComplete="new-password"
              />
            </div>

            {error && <div className="form-error">{error}</div>}

            <button type="submit" className="final-submit" disabled={loading}>
              {loading ? (
                <Loader size={20} className="spin" />
              ) : (
                <>Commencer maintenant <ArrowRight size={18} /></>
              )}
            </button>

            <p className="form-note">
              ✨ Gratuit pendant 7 jours • Sans engagement • Annulation facile
            </p>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="onboarding-quiz">
      <div className="quiz-progress-bar">
        <div className="progress-fill" style={{ width: `${progress}%` }}></div>
      </div>

      <div className="quiz-top">
        {step > 0 && (
          <button className="quiz-back-btn" onClick={handleBack}>
            <ArrowLeft size={20} />
          </button>
        )}
        <span className="quiz-counter">{step + 1} sur {totalSteps}</span>
        <button className="quiz-skip-btn" onClick={onSkip}>
          Passer
        </button>
      </div>

      <div className="quiz-body">
        <h1>{currentQuestion.question}</h1>
        {currentQuestion.subtitle && (
          <p className="quiz-subtitle">{currentQuestion.subtitle}</p>
        )}

        <div className="quiz-options">
          {currentQuestion.options.map((option) => (
            <button
              key={option.id}
              className={`quiz-option ${answers[currentQuestion.id] === option.id ? 'selected' : ''}`}
              onClick={() => handleAnswer(option.id)}
            >
              <span className="option-emoji">{option.emoji}</span>
              <div className="option-content">
                <span className="option-label">{option.label}</span>
                {option.desc && <span className="option-desc">{option.desc}</span>}
              </div>
              <ChevronRight size={20} className="option-arrow" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default OnboardingQuiz;
